import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AGENT_UPDATE_MODE_STORAGE_KEY,
  WORKSPACE_KEY_STORAGE_KEY,
  type AgentUpdateMode,
  buildWorkspaceKeyHeaders,
  clearStoredWorkspaceKey,
  getAgentApiBaseUrl,
  getDefaultSelectedEventId,
  getDefaultSelectedTaskId,
  filterAgentUpdateRecordsByTaskId,
  getInitialAgentUpdateMode,
  getTaskTimelineEmptyState,
  getVisibleAgentTimelineEntries,
  getVisibleAgentUpdateRecords,
  isValidWorkspaceKey,
  isTurnInProgress,
  mergeAgentUpdateRecords,
  parseAgentUpdateSseRecords,
  shouldAppendLiveRecordToSelectedTask,
  summarizeRecords,
} from "../../agent-updates";
import type {
  AgentPromptCommandRecord,
  AgentTaskRecord,
  AgentTasksSnapshot,
  AgentUpdateRecord,
  AgentUpdatesSnapshot,
} from "../../../../../shared/agent";

export type ConnectionState = "connecting" | "connected" | "disconnected";

const AGENT_API_BASE_URL = getAgentApiBaseUrl({
  queryString: location.search,
  storedValue: localStorage.getItem("workanywhere.agentApiBaseUrl"),
  envValue: process.env.BACKEND_URL,
});

export function useAgentUpdates() {
  const [workspaceKey, setWorkspaceKeyState] = useState(() => localStorage.getItem(WORKSPACE_KEY_STORAGE_KEY) ?? "");
  const [mode, setModeState] = useState<AgentUpdateMode>(() =>
    getInitialAgentUpdateMode(localStorage.getItem(AGENT_UPDATE_MODE_STORAGE_KEY)),
  );
  const [tasks, setTasks] = useState<AgentTaskRecord[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [records, setRecords] = useState<AgentUpdateRecord[]>([]);
  const [promptCommands, setPromptCommands] = useState<AgentPromptCommandRecord[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>("connecting");
  const [taskLoading, setTaskLoading] = useState(true);
  const [taskErrorMessage, setTaskErrorMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const modeRef = useRef(mode);
  const selectedTaskIdRef = useRef<string | null>(null);
  const timelineEndRef = useRef<HTMLDivElement>(null);
  const hasWorkspaceKey = isValidWorkspaceKey(workspaceKey);

  const setWorkspaceKey = useCallback((nextWorkspaceKey: string) => {
    const normalizedWorkspaceKey = nextWorkspaceKey.trim();
    if (isValidWorkspaceKey(normalizedWorkspaceKey)) {
      localStorage.setItem(WORKSPACE_KEY_STORAGE_KEY, normalizedWorkspaceKey);
      setWorkspaceKeyState(normalizedWorkspaceKey);
    }
  }, []);

  const clearWorkspaceKey = useCallback(() => {
    clearStoredWorkspaceKey();
    setWorkspaceKeyState("");
    setTasks([]);
    setSelectedTaskId(null);
    setRecords([]);
    setPromptCommands([]);
    setSelectedEventId(null);
    setTaskErrorMessage(null);
    setErrorMessage(null);
    setConnectionState("disconnected");
    setTaskLoading(false);
  }, []);

  const loadTasks = useCallback(async () => {
    if (!hasWorkspaceKey) {
      setTasks([]);
      setTaskLoading(false);
      return;
    }

    setTaskLoading(true);

    try {
      const response = await fetch(`${AGENT_API_BASE_URL}/agent/tasks`, {
        headers: buildWorkspaceKeyHeaders(workspaceKey),
      });

      if (!response.ok) {
        throw new Error(`Tasks request failed with ${response.status}`);
      }

      const snapshot = (await response.json()) as AgentTasksSnapshot;

      setTasks(snapshot.tasks);
      setSelectedTaskId((current) => getDefaultSelectedTaskId(snapshot.tasks, current));
      setTaskErrorMessage(null);
    } catch (error) {
      setTaskErrorMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setTaskLoading(false);
    }
  }, [hasWorkspaceKey, workspaceKey]);

  useEffect(() => {
    selectedTaskIdRef.current = selectedTaskId;
  }, [selectedTaskId]);

  useEffect(() => {
    let cancelled = false;

    async function loadSelectedTaskSnapshot() {
      if (!selectedTaskId) {
        setRecords([]);
        setPromptCommands([]);
        setSelectedEventId(null);
        return;
      }

      if (!hasWorkspaceKey) {
        setRecords([]);
        setPromptCommands([]);
        setSelectedEventId(null);
        return;
      }

      try {
        const url = new URL(`${AGENT_API_BASE_URL}/agent/updates`);
        url.searchParams.set("task_id", selectedTaskId);
        const response = await fetch(url, {
          headers: buildWorkspaceKeyHeaders(workspaceKey),
        });

        if (!response.ok) {
          throw new Error(`Snapshot request failed with ${response.status}`);
        }

        const snapshot = (await response.json()) as AgentUpdatesSnapshot;

        if (!cancelled) {
          const taskRecords = filterAgentUpdateRecordsByTaskId(snapshot.events, selectedTaskId);
          setRecords(taskRecords);
          setPromptCommands(snapshot.promptCommands ?? []);
          setSelectedEventId((current) => getDefaultSelectedEventId(taskRecords, current, modeRef.current));
          setErrorMessage(null);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(error instanceof Error ? error.message : String(error));
        }
      }
    }

    void loadSelectedTaskSnapshot();

    return () => {
      cancelled = true;
    };
  }, [hasWorkspaceKey, selectedTaskId, workspaceKey]);

  useEffect(() => {
    if (!hasWorkspaceKey) {
      setConnectionState("disconnected");
      void loadTasks();
      return;
    }

    let cancelled = false;
    const abortController = new AbortController();

    void loadTasks();
    void connectUpdateStream(abortController.signal);

    return () => {
      cancelled = true;
      abortController.abort();
    };

    async function connectUpdateStream(signal: AbortSignal) {
      try {
        const response = await fetch(`${AGENT_API_BASE_URL}/agent/updates/stream`, {
          headers: buildWorkspaceKeyHeaders(workspaceKey),
          signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`Live stream failed with ${response.status}`);
        }

        if (!cancelled) {
          setConnectionState("connected");
          setErrorMessage(null);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        try {
          while (!cancelled) {
            const result = await reader.read();
            if (result.done) {
              break;
            }

            buffer += decoder.decode(result.value, { stream: true });
            const boundary = buffer.lastIndexOf("\n\n");
            if (boundary === -1) {
              continue;
            }

            const complete = buffer.slice(0, boundary + 2);
            buffer = buffer.slice(boundary + 2);

            for (const record of parseAgentUpdateSseRecords(complete)) {
              handleLiveRecord(record);
            }
          }
        } finally {
          reader.releaseLock();
        }
      } catch (error) {
        if (!cancelled && !signal.aborted) {
          setConnectionState("disconnected");
          setErrorMessage(error instanceof Error ? error.message : "Live stream disconnected");
        }
      }
    }

    function handleLiveRecord(record: AgentUpdateRecord) {
      void loadTasks();

      if (!shouldAppendLiveRecordToSelectedTask(record, selectedTaskIdRef.current)) {
        return;
      }

      setRecords((current) => {
        const merged = mergeAgentUpdateRecords(current, [record]);
        if (modeRef.current === "developer") {
          setSelectedEventId(record.id);
        } else {
          setSelectedEventId((selected) => getDefaultSelectedEventId(merged, selected, modeRef.current));
        }
        return merged;
      });
    }
  }, [hasWorkspaceKey, loadTasks, workspaceKey]);

  const setMode = useCallback(
    (nextMode: AgentUpdateMode) => {
      modeRef.current = nextMode;
      localStorage.setItem(AGENT_UPDATE_MODE_STORAGE_KEY, nextMode);
      setModeState(nextMode);
      setSelectedEventId((current) => (nextMode === "normal" ? null : getDefaultSelectedEventId(records, current, nextMode)));
    },
    [records],
  );

  const summary = useMemo(() => summarizeRecords(records), [records]);
  const visibleRecords = useMemo(() => getVisibleAgentUpdateRecords(records, mode), [mode, records]);
  const visibleTimelineEntries = useMemo(
    () => getVisibleAgentTimelineEntries({ records, promptCommands, mode }),
    [mode, promptCommands, records],
  );
  const turnInProgress = useMemo(() => isTurnInProgress(records), [records]);
  const selectedTask = useMemo(
    () => tasks.find((task) => task.id === selectedTaskId) ?? null,
    [selectedTaskId, tasks],
  );
  const selectedRecord = visibleRecords.find((record) => record.id === selectedEventId) ?? null;
  const lastReceivedAt = records.at(-1)?.receivedAt ?? null;
  const emptyState = turnInProgress ? null : getTaskTimelineEmptyState(tasks, selectedTask, visibleTimelineEntries);

  const appendPromptCommand = useCallback((command: AgentPromptCommandRecord) => {
    setPromptCommands((current) => {
      if (current.some((existing) => existing.id === command.id)) {
        return current.map((existing) => (existing.id === command.id ? command : existing));
      }

      return [...current, command];
    });
  }, []);

  useEffect(() => {
    timelineEndRef.current?.scrollIntoView({ block: "end" });
  }, [turnInProgress, visibleTimelineEntries.length]);

  return {
    agentApiBaseUrl: AGENT_API_BASE_URL,
    connectionState,
    closeInspector: () => setSelectedEventId(null),
    emptyState,
    errorMessage,
    lastReceivedAt,
    mode,
    records,
    selectedRecord,
    selectedTask,
    selectedTaskId,
    setMode,
    selectTask: setSelectedTaskId,
    selectRecord: setSelectedEventId,
    summary,
    taskErrorMessage,
    taskLoading,
    tasks,
    timelineEndRef,
    turnInProgress,
    visibleRecords,
    visibleTimelineEntries,
    workspaceKey,
    hasWorkspaceKey,
    setWorkspaceKey,
    clearWorkspaceKey,
    appendPromptCommand,
  };
}
